import { createDashHttpClient } from '@/apis/dash.http';
import { env } from '@/env';

export const libraryHttp = createDashHttpClient(env.libraryApiUrl);

const campaignCreationHttp = createDashHttpClient(env.libraryApiUrl, {
  decamelizeIgnoreKeys: ['CREATOR_SUMMARY', 'OWNED_SUMMARY', 'SOCIAL_ADS_SUMMARY'],
});

const instagramHttp = createDashHttpClient(env.instagramApiUrl, {
  camelizeKeys: true,
});

export async function createCampaign({
  brandId,
  name,
  hashtagIds,
  startDate,
  endDate,
  benchmarkCampaigns,
  toplineMetrics,
}) {
  return campaignCreationHttp.post(`/brands/${brandId}/campaigns`, {
    name,
    hashtagIds,
    startDate,
    endDate,
    benchmarkCampaigns,
    toplineMetrics,
  });
}

export async function getCampaign({ brandId, campaignId }) {
  return libraryHttp.get(`/brands/${brandId}/campaigns/${campaignId}`);
}

export async function updateCampaign({
  brandId,
  campaignId,
  name,
  hashtagIds,
  benchmarkCampaigns,
  toplineMetrics,
  googleAnalyticsSelectedCampaigns,
  creatorIds,
  creatorFilters,
  ttcmSparkAdsAuthorizationDays,
}) {
  return campaignCreationHttp.patch(`/brands/${brandId}/campaigns/${campaignId}`, {
    name,
    hashtagIds,
    benchmarkCampaigns,
    toplineMetrics,
    googleAnalyticsSelectedCampaigns,
    creatorIds,
    creatorFilters,
    ttcmSparkAdsAuthorizationDays,
  });
}

export async function deleteCampaign({ brandId, campaignId }) {
  return libraryHttp.delete(`/brands/${brandId}/campaigns/${campaignId}`);
}

export async function getVideos({
  brandId,
  campaignId,
  limit,
  sort,
  sourceCreatedOnOrAfter,
  sourceCreatedOnOrBefore,
}) {
  return libraryHttp.get(`/brands/${brandId}/campaigns/${campaignId}/media`, {
    params: {
      postType: 'VIDEO',
      sort,
      sourceCreatedOnOrAfter,
      sourceCreatedOnOrBefore,
      limit,
    },
  });
}

export async function getPosts({
  brandId,
  campaignId,
  limit,
  sort,
  sourceCreatedOnOrAfter,
  sourceCreatedOnOrBefore,
}) {
  return libraryHttp.get(`/brands/${brandId}/campaigns/${campaignId}/media`, {
    params: {
      postType: 'POST',
      sort,
      sourceCreatedOnOrAfter,
      sourceCreatedOnOrBefore,
      limit,
    },
  });
}

export async function getTopKeywords(
  { brandId, campaignId, sourceCreatedOnOrAfter, sourceCreatedOnOrBefore, cloudTypes },
  config = {},
) {
  return libraryHttp.get(`/brands/${brandId}/campaigns/${campaignId}/top_keywords`, {
    params: {
      sourceCreatedOnOrAfter,
      sourceCreatedOnOrBefore,
      cloudType: cloudTypes,
    },
    ...config,
  });
}

export async function addOwnedMediaToCampaign({ brandId, campaignId, mediaIds, galleryIds }) {
  return libraryHttp.post(`/brands/${brandId}/campaigns/${campaignId}/media`, {
    mediaIds,
    galleryIds,
  });
}

export async function addUgcMediaToCampaign({ brandId, campaignId, mediaIds, galleryIds }) {
  return libraryHttp.post(`/brands/${brandId}/campaigns/${campaignId}/ugc`, {
    mediaIds,
    galleryIds,
  });
}

export async function addMediaToCampaignRelationship({
  brandId,
  campaignId,
  relationshipId,
  mediaIds,
}) {
  return libraryHttp.post(
    `/brands/${brandId}/campaigns/${campaignId}/relationships/${relationshipId}/media`,
    { mediaIds },
  );
}

export async function getSummaryCampaigns({ brandId, creatorId }) {
  return libraryHttp.get(`/brands/${brandId}/campaigns`, {
    params: { summary: true, creator_id: creatorId },
  });
}

export async function getAllCampaigns({ brandId }) {
  return libraryHttp.get(`/brands/${brandId}/campaigns`);
}

export async function getCampaigns({
  url,
  brandId,
  limit,
  summary,
  search,
  sort,
  ids,
  creatorId,
  creatorCampaignStatus,
  hasCreators,
}) {
  let constructedUrl = `/brands/${brandId}/campaigns?limit=${limit}`;
  if (summary) constructedUrl = `${constructedUrl}&summary=${summary}`;
  if (search) constructedUrl = `${constructedUrl}&search=${search}`;
  if (sort) constructedUrl = `${constructedUrl}&sort=${sort}`;
  if (ids) constructedUrl = `${constructedUrl}&ids=${ids}`;
  if (creatorId) constructedUrl = `${constructedUrl}&creator_id=${creatorId}`;
  if (creatorCampaignStatus)
    constructedUrl = `${constructedUrl}&creator_campaign_status=${creatorCampaignStatus}`;
  if (hasCreators) constructedUrl = `${constructedUrl}&has_creators=${hasCreators}`;
  return libraryHttp.get(url || constructedUrl);
}

export async function getCampaignsForBrands({ brandIds, search, limit }, config = {}) {
  return libraryHttp.post('/brands/campaigns/search', { brandIds, search, limit }, config);
}

export async function getCampaignRelationships(
  { brandId, campaignId, startDate, endDate },
  config = {},
) {
  return libraryHttp.get(`/brands/${brandId}/campaigns/${campaignId}/relationships`, {
    params: {
      startDate,
      endDate,
    },
    ...config,
  });
}

export async function getCampaignUgcMedia(
  {
    brandId,
    campaignId,
    url,
    sort,
    source,
    sourceCreatedOnOrAfter,
    sourceCreatedOnOrBefore,
    limit,
  },
  config = {},
) {
  return libraryHttp.get(url || `/brands/${brandId}/campaigns/${campaignId}/ugc`, {
    params: {
      sort,
      source: !url ? source : undefined,
      sourceCreatedOnOrAfter,
      sourceCreatedOnOrBefore,
      limit: !url ? limit : undefined,
    },
    ...config,
  });
}

export async function getCampaignChannelMedia(
  {
    brandId,
    campaignId,
    url,
    sort,
    source,
    sourceCreatedOnOrAfter,
    sourceCreatedOnOrBefore,
    limit,
  },
  config = {},
) {
  return libraryHttp.get(url || `/brands/${brandId}/campaigns/${campaignId}/media`, {
    params: {
      sort,
      source: !url ? source : undefined,
      sourceCreatedOnOrAfter,
      sourceCreatedOnOrBefore,
      limit: !url ? limit : undefined,
    },
    ...config,
  });
}

export async function getCampaignStats(
  { brandId, campaignId, startDate, endDate, scale },
  config = {},
) {
  return libraryHttp.get(`/brands/${brandId}/campaigns/${campaignId}/stats`, {
    params: {
      startDate,
      endDate,
      scale,
    },
    ...config,
  });
}

export async function getCampaignStatsForOverview(
  { brandId, campaignId, startDate, endDate },
  config = {},
) {
  return libraryHttp.get(`/brands/${brandId}/campaigns/${campaignId}/stats/overview`, {
    params: {
      startDate,
      endDate,
    },
    ...config,
  });
}

export async function getCampaignBenchmarksOwnedSummary({ brandId, campaignId }, config = {}) {
  return libraryHttp.get(`/brands/${brandId}/campaigns/${campaignId}/benchmarks/owned_summary`, {
    ...config,
  });
}

export async function getCampaignBenchmarksCreatorSummary({ brandId, campaignId }, config = {}) {
  return libraryHttp.get(`/brands/${brandId}/campaigns/${campaignId}/benchmarks/creator_summary`, {
    ...config,
  });
}

export async function getCampaignBenchmarksSocialAdsSummary({ brandId, campaignId }, config = {}) {
  return libraryHttp.get(
    `/brands/${brandId}/campaigns/${campaignId}/benchmarks/social_ads_summary`,
    {
      ...config,
    },
  );
}

export async function removeOwnedMediaFromCampaign({ brandId, campaignId, mediaIds }) {
  return libraryHttp.delete(`/brands/${brandId}/campaigns/${campaignId}/media`, {
    params: {
      mediaIds: mediaIds.toString(),
    },
  });
}

export async function removeRelationshipMediaFromCampaign({
  brandId,
  campaignId,
  relationshipId,
  mediaIds,
}) {
  return libraryHttp.delete(
    `/brands/${brandId}/campaigns/${campaignId}/relationships/${relationshipId}/media`,
    {
      params: {
        mediaIds: mediaIds.toString(),
      },
    },
  );
}

export async function removeUgcMediaFromCampaign({ brandId, campaignId, mediaIds }) {
  return libraryHttp.delete(`/brands/${brandId}/campaigns/${campaignId}/ugc`, {
    params: {
      mediaIds: mediaIds.toString(),
    },
  });
}

export async function getRelationships({ brandId, campaignId, relationshipIds }) {
  return libraryHttp.get(`/brands/${brandId}/campaigns/${campaignId}/relationships`, {
    params: {
      relationshipIds,
    },
  });
}

export async function addRelationship({ brandId, campaignId, relationshipId, mediaIds }) {
  return libraryHttp.post(
    `/brands/${brandId}/campaigns/${campaignId}/relationships/${relationshipId}/media`,
    {
      mediaIds,
    },
  );
}

export async function deleteRelationship({ brandId, campaignId, relationshipId, mediaIds }) {
  return libraryHttp.delete(
    `/brands/${brandId}/campaigns/${campaignId}/relationships/${relationshipId}/media`,
    {
      params: {
        media_ids: mediaIds,
      },
    },
  );
}

export async function getMonitoredHashtags({ brandId }) {
  return instagramHttp.get(`/brands/${brandId}/instagram/hashtags`);
}

export async function getRelationshipPosts({ brandId, relationshipId, mediaIds }) {
  return instagramHttp.get(
    `/brands/${brandId}/instagram/relationships/${relationshipId}/relationship_posts?library_media_ids=${mediaIds}`,
    {
      params: {
        libraryMediaIds: mediaIds,
        sort: '-TIMESTAMP',
      },
    },
  );
}

export async function downloadSpreadsheet({
  brandId,
  campaignId,
  socketId,
  startDate,
  endDate,
  isAllTime = false,
}) {
  return libraryHttp.post(`/brands/${brandId}/campaigns/${campaignId}/stats/report`, {
    socketId,
    startDate,
    endDate,
    isAllTime,
  });
}

export async function getGoogleAnalyticsReport(
  { brandId, startDate, endDate, metrics, dimensions, dimensionFilter },
  config = {},
) {
  return libraryHttp.post(
    `/brands/${brandId}/google_analytics/account/report`,
    {
      dateRange: { startDate, endDate },
      metrics,
      dimensions,
      dimensionFilter,
    },
    config,
  );
}

export async function getCampaignSentiment(
  { brandId, campaignId, startDate, endDate },
  config = {},
) {
  return libraryHttp.get(`/brands/${brandId}/campaigns/${campaignId}/stats/sentiment`, {
    params: { startDate, endDate },
    ...config,
  });
}

export async function getCampaignAdCampaigns({ brandId, campaignId }, config = {}) {
  return libraryHttp.get(`/brands/${brandId}/campaigns/${campaignId}/ad_campaigns`, config);
}

export async function updateSelectedAdCampaigns({ brandId, campaignId, adCampaigns }, config = {}) {
  return libraryHttp.put(
    `/brands/${brandId}/campaigns/${campaignId}/ad_campaigns`,
    { adCampaigns },
    config,
  );
}

export async function getCampaignAds(
  { brandId, campaignId, source, adCampaignIds, sort, limit, offset = 0 },
  config = {},
) {
  return libraryHttp.post(
    `/brands/${brandId}/campaigns/${campaignId}/ads/search`,
    { source, source_campaign_ids: adCampaignIds, sort, limit, offset },
    config,
  );
}
